import { useMemo, useState } from 'react';
import { Box, CircularProgress, Grid, IconButton, Typography } from '@material-ui/core';
import { serviceLevelsColor, serviceLevels, currencies, seasonsPrices } from 'config/constants';
import { useBookingAgenda, useBookingAgendaFunctions } from '../../contexts/BookingAgendaContext';
import { useTheme } from '@material-ui/core/styles';
import AddCircleOutlineRoundedIcon from '@material-ui/icons/AddCircleOutlineRounded';
import DataTable from 'components/DataTable';
import DefaultButton from 'components/DefaultButton';
import DefaultModal from 'components/DefaultModal';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';
import ExternalReservation from '../ExternalReservation';
import placeholder from '_assets/common/placeholer.png';

const headCells = [
  { id: 'id', label: 'ID Client', sortable: false },
  { id: 'lastName', label: 'Nom', sortable: true },
  { id: 'firstName', label: 'Prénom', sortable: true },
  { id: 'quantity', label: 'Places occupées', sortable: true },
  { id: 'edit', label: 'Editer', sortable: false },
];

export default function ReservationDetail({ isOpen, onClose }) {
  const theme = useTheme();

  const { clients, isDeleting, isLoadingAgenda, selectedAppointment } = useBookingAgenda();
  const { removeBooking } = useBookingAgendaFunctions();

  const [selectedClient, setSelectedClient] = useState(null);
  const [isOpenClient, setIsOpenClient] = useState();

  const canBook = useMemo(() => {
    if (!selectedAppointment) return false;
    if (selectedAppointment.capacity === 100) return false;
    if (selectedAppointment.startDate < new Date()) return false;
    if (selectedAppointment.service.status === 'disabled') return false;
    return !(
      selectedAppointment.service.status === 'blocked' &&
      selectedAppointment.startDate > new Date(selectedAppointment.service.lastBookingDate)
    );
  }, [selectedAppointment]);

  const formattedClient = useMemo(() => {
    return clients.map((value) => ({
      ...value,
      edit: {
        component: (
          <>
            <IconButton
              onClick={() => {
                setIsOpenClient(true);
                setSelectedClient(value);
              }}
            >
              <EditIcon color="primary" />
            </IconButton>
            <IconButton
              onClick={async () => {
                await removeBooking(value.bookingId);
              }}
            >
              <DeleteIcon style={{ color: theme.palette.tertiary.main }} />
            </IconButton>
          </>
        ),
      },
    }));
  }, [clients, theme, removeBooking]);

  function handleCloseClient() {
    setIsOpenClient(false);
    setSelectedClient(null);
  }

  const renderLevel = () => {
    const { service } = selectedAppointment;

    if (!service?.level)
      return (
        <Typography color="grey" pill>
          Inconnu
        </Typography>
      );

    const { label } = serviceLevels.find((s) => s.value === service.level);
    return (
      <Typography color={serviceLevelsColor[label]} pill>
        {label}
      </Typography>
    );
  };

  if (!selectedAppointment) return null;

  return (
    <>
      <DefaultModal info infoButtonText="Terminer" open={isOpen} title="Réservations" onClose={onClose}>
        <Grid container style={{ marginBottom: '8px' }}>
          <Grid container item xs={7}>
            <img
              src={selectedAppointment.service.image ?? placeholder}
              alt="Service"
              height={!selectedAppointment.service.image ? '180px' : 'inherit'}
              width="75%"
              style={{ marginTop: '5%' }}
            />
          </Grid>

          <Grid container item xs={5}>
            <Grid container item xs={12} alignItems="center">
              <Typography variant="h5">{selectedAppointment.service.name}</Typography>
            </Grid>

            <Grid item xs={12}>
              <Box style={{ marginBottom: '16px' }}>
                <Typography>Niveau</Typography>
                {renderLevel()}
              </Box>

              <Typography>Prix TTC - {seasonsPrices[selectedAppointment.service.seasonPrice]}</Typography>
              {`${selectedAppointment.service.currentPrice} ${currencies[selectedAppointment.service.currency]}`}
            </Grid>
          </Grid>
        </Grid>

        <DataTable data={formattedClient} loading={isDeleting || isLoadingAgenda} headCells={headCells} />

        <DefaultButton
          color="secondary"
          disabled={isLoadingAgenda || !canBook}
          startIcon={<AddCircleOutlineRoundedIcon color={!canBook ? 'default' : 'secondary'} />}
          style={{ fontSize: 21, fontWeight: 300, marginTop: '8px' }}
          variant="outlined"
          onClick={() => setIsOpenClient(true)}
        >
          {isLoadingAgenda ? <CircularProgress color="secondary" size={20} /> : 'Réservation'}
        </DefaultButton>
      </DefaultModal>

      <ExternalReservation client={selectedClient} isOpen={isOpenClient} onClose={handleCloseClient} />
    </>
  );
}
